import React, { useState } from "react";
import { IoMdEye } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  getAllRestaurants,
  updateManagerChatAccess,
} from "../../../api/admin/restaurantManagement";
import Pagination from "../../../components/common/Pagination";
import { useTranslation } from "../../../hooks/useTranslation";

const RestaurantManagement = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 10;
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["restaurants", page],
    queryFn: () => getAllRestaurants({ page, limit }),
    keepPreviousData: true,
  });

  const chatAccessMutation = useMutation({
    mutationFn: (data) => updateManagerChatAccess(data),
    onSuccess: () => {
      toast.success(t("restaurantManagement").messages.updateSuccess);
      queryClient.invalidateQueries(["restaurants"]);
    },
    onError: () => {
      toast.error(t("restaurantManagement").messages.updateFailed);
    },
  });

  const handleToggleChatAccess = (restaurantId, currentValue, status) => {
    if (status === "rejected") {
      return false;
    }

    chatAccessMutation.mutate({
      restaurantId,
      managerChatAccess: !currentValue,
    });
  };

  const handleToggleMenuOnlyMode = (restaurantId, currentValue, status) => {
    if (status === "rejected") {
      return false;
    }

    chatAccessMutation.mutate({
      restaurantId,
      menuOnlyMode: !currentValue,
    });
  };

  const restaurants = data?.data?.restaurants || [];
  const pagination = data?.data?.pagination || {};

  return (
    <>
      <div className="flex gap-[10px] items-center">
        <div
          onClick={() => navigate(-1)}
          className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]"
        >
          <IoIosArrowRoundBack size={24} className="text-[#fff]" />
        </div>
        <h2 className="text-[22px] font-medium">
          {t("restaurantManagement").title}
        </h2>
      </div>

      <div className="mt-[31px]">
        <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden mt-[28px] py-[20px] sm:px-[30px] px-[15px] bg-white">
          <h2 className="text-base font-semibold">
            {t("restaurantManagement").approveNewRestaurant}
          </h2>

          {isLoading ? (
            <div className="flex items-center justify-center h-screen">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : isError ? (
            <p className="text-center mt-6 text-red-500">{error.message}</p>
          ) : (
            <div className="overflow-x-auto md:mt-[30px] mt-[30px]">
              <table className="min-w-full">
                <thead className="bg-[#FFFAF2]">
                  <tr>
                    <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                      {t("restaurantManagement").tableHeaders.restaurantName}
                    </th>
                    <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                      {t("restaurantManagement").tableHeaders.category}
                    </th>
                    <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                      {t("restaurantManagement").tableHeaders.status}
                    </th>
                    <th className="text-center text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                      {t("restaurantManagement").tableHeaders.managerChatAccess}
                    </th>
                    <th className="text-center text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                      {t("restaurantManagement").tableHeaders.menuOnlyMode}
                    </th>
                    <th className="text-center text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                      {t("restaurantManagement").tableHeaders.platformFee}
                    </th>
                    <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                      {t("restaurantManagement").tableHeaders.actions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {restaurants.map((restaurant) => (
                    <tr
                      key={restaurant._id}
                      className="border-t border-[#E4E4E4]"
                    >
                      <td className="py-[12px] px-[16px] text-[17px] text-black">
                        {restaurant.name}
                      </td>
                      <td className="py-[12px] px-[16px] text-[17px] text-black whitespace-nowrap">
                        {t("restaurantManagement").restaurantCategory}
                      </td>
                      <td className="py-[12px] px-[16px] text-[17px] text-black whitespace-nowrap">
                        <div
                          className={`p-[2px_8px_2px_8px] rounded-full w-fit  text-center text-sm  text-white ${
                            restaurant.verificationStatus === "approved"
                              ? "bg-[#4CAF50]"
                              : restaurant.verificationStatus === "rejected"
                              ? "bg-[#FF0000]"
                              : "bg-[#FF9D00]"
                          }`}
                        >
                          {restaurant.verificationStatus
                            .charAt(0)
                            .toUpperCase() +
                            restaurant.verificationStatus.slice(1)}
                        </div>
                      </td>
                      <td className="py-[12px] px-[16px]">
                        <div
                          onClick={() =>
                            handleToggleChatAccess(
                              restaurant._id,
                              restaurant.managerChatAccess,
                              restaurant.verificationStatus
                            )
                          }
                          className={`w-[48px] h-[20px] mx-auto flex items-center rounded-full p-1 cursor-pointer transition-colors ${
                            restaurant.managerChatAccess
                              ? "bg-[#4CAF50]"
                              : "bg-[#e1e1e1]"
                          }`}
                        >
                          <div
                            className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${
                              restaurant.managerChatAccess
                                ? "translate-x-6"
                                : "translate-x-0"
                            }`}
                          />
                        </div>
                      </td>
                      <td className="py-[12px] px-[16px]">
                        <div
                          onClick={() =>
                            handleToggleMenuOnlyMode(
                              restaurant._id,
                              restaurant.menuOnlyMode,
                              restaurant.verificationStatus
                            )
                          }
                          className={`w-[48px] h-[20px] mx-auto flex items-center rounded-full p-1 cursor-pointer transition-colors ${
                            restaurant.menuOnlyMode
                              ? "bg-[#4CAF50]"
                              : "bg-[#e1e1e1]"
                          }`}
                        >
                          <div
                            className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${
                              restaurant.menuOnlyMode
                                ? "translate-x-6"
                                : "translate-x-0"
                            }`}
                          />
                        </div>
                      </td>
                      <td className="py-[12px] px-[16px] text-[17px] text-black whitespace-nowrap">
                        {restaurant?.platformFee}%
                      </td>
                      <td className="py-[12px] px-[16px]">
                        <Link
                          to={`/restaurantdetailpage/${restaurant._id}`}
                          className="text-black w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2]"
                        >
                          <IoMdEye className="text-base text-black" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            setPage={setPage}
          />
        </div>
      </div>
    </>
  );
};

export default RestaurantManagement;
